import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { getIn } from 'formik';
import PropTypes from 'prop-types';
import momentTz from 'moment-timezone';
import { getTimezoneDate } from 'utils';
import DatePicker from 'react-datepicker';
import DatePickerWrapper from './DateTimePicker.styled';
import TextFieldStyled from './TextField.styled';
import 'react-datepicker/dist/react-datepicker.css';
import { ErrorMessage } from 'components/FormFields';
import { GlobalStyles } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { datePickerStyles } from './DateTimePicker.styled';

const TIME_IN_SECONDS = 24 * 60 * 60;

const DateTimePickerField = ({
  id,
  utc,
  day,
  time,
  label,
  hours,
  format,
  maxDate,
  minDate,
  timezone,
  required,
  editable,
  disabled,
  InputProps,
  dateFormat,
  timeFormat,
  isClearable,
  isStateless = false,
  asyncErrors,
  handleChange,
  timeIntervals,
  showTimeSelect,
  popperModifiers,
  popperPlacement,
  showTimeSelectOnly,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  portalId,
  ...props
}) => {
  const theme = useTheme();
  const datePickerGlobalStyles = useMemo(() => datePickerStyles(theme), [theme]);
  const [datetime, setDatetime] = useState(value ? getTimezoneDate(value, timezone) : null);

  const handleOnChange = (_date) => {
    setDatetime(_date);
    let date = timezone
      ? momentTz(_date).tz(timezone, true).format()
      : moment(_date).format(format);
    if (utc) {
      date = timezone ? moment(date).utc().format() : moment(date).toISOString(true);
    }
    if (time) {
      // Get hour
      const hours = moment(_date).get('hour') * 60 * 60;
      const minutes = moment(_date).get('minutes') * 60;
      const time = (hours + minutes) / TIME_IN_SECONDS;
      // Get the integer part absolute to evaluate as "day"
      const dayValue = Math.trunc(Math.abs(day));
      // Append the fraction of n/24
      date = dayValue + time;
    }
    setFieldValue(name, _date ? date : null);
    if (handleChange) {
      handleChange(name, _date);
    }
  };

  const handleShowError = () => {
    if (asyncErrors) {
      return asyncErrors;
    } else if (errors && getIn(errors, name) && getIn(touched, name)) {
      return getIn(errors, name);
    }
  };

  useEffect(() => {
    if (day && value && !isStateless) {
      const time = Math.round((value - Math.round(value)) * TIME_IN_SECONDS);
      const minutes = time / 60;
      const hours = Math.trunc(minutes / 60);
      const date = getTimezoneDate(
        moment().set({
          hour: hours,
          minutes: minutes - hours * 60,
        }),
        timezone
      );
      if (time) {
        handleOnChange(date);
      }
      setDatetime(date);
    }
  }, [day, value]); // eslint-disable-line

  useEffect(() => {
    if (value && !day && !hours && !isStateless) {
      const date = getTimezoneDate(value, timezone);
      if (!isNaN(date.getTime())) {
        setDatetime(value ? date : null);
      }
    }
  }, [value, timezone, day, hours, isStateless]);

  return (
    <DatePickerWrapper>
      <GlobalStyles styles={datePickerGlobalStyles} />
      <DatePicker
        name={name}
        minDate={minDate}
        maxDate={maxDate}
        timeCaption="Time"
        selected={datetime}
        disabled={disabled}
        id={id || 'datePicker'}
        timeIntervals={timeIntervals}
        isClearable={isClearable}
        timeFormat={timeFormat}
        required={required}
        // formats allowed to read, only first one will be used to show date
        dateFormat={[dateFormat, 'MMddyyyy']}
        customInput={
          <TextFieldStyled
            label={label}
            margin="none"
            InputProps={InputProps || {}}
            {...props}
            variant="standard"
            editable={(editable || false).toString()}
          />
        }
        showTimeSelect={showTimeSelect}
        showTimeSelectOnly={showTimeSelectOnly}
        popperPlacement={popperPlacement}
        popperModifiers={popperModifiers}
        portalId={portalId}
        onChange={(date) => handleOnChange(date)}
      />
      {handleShowError() && <ErrorMessage>{handleShowError()}</ErrorMessage>}
    </DatePickerWrapper>
  );
};

DateTimePickerField.propTypes = {
  utc: PropTypes.bool,
  maxDate: PropTypes.any,
  minDate: PropTypes.any,
  format: PropTypes.string,
  disabled: PropTypes.bool,
  timeFormat: PropTypes.string,
  dateFormat: PropTypes.string,
  asyncErrors: PropTypes.string,
  timeIntervals: PropTypes.number,
  day: PropTypes.number,
  showTimeSelect: PropTypes.bool,
  time: PropTypes.bool,
  showTimeSelectOnly: PropTypes.bool,
  isClearable: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  InputProps: PropTypes.any,
  popperPlacement: PropTypes.oneOf(['bottom-start', 'bottom-end', 'top-start', 'top-end']),
  id: PropTypes.string,
  label: PropTypes.string,
  hours: PropTypes.number,
  timezone: PropTypes.string,
  required: PropTypes.bool,
  editable: PropTypes.bool,
  isStateless: PropTypes.bool,
  handleChange: PropTypes.func,
  popperModifiers: PropTypes.object,
  portalId: PropTypes.string,
};

DateTimePickerField.defaultProps = {
  popperPlacement: 'bottom-start',
  dateFormat: 'MMMM d, yyyy h:mm aa',
  utc: false,
  showTimeSelectOnly: false,
  format: '',
  timeFormat: 'h:mm aa',
  showTimeSelect: true,
  timeIntervals: 15,
  day: 0,
  disabled: false,
  time: false,
  isClearable: false,
  required: false,
  form: {
    errors: {},
    touched: {},
  },
  portalId: 'root',
};

export default DateTimePickerField;
